import { SignInRequestType, SigninResponseType } from 'src/models/Account/Signin';
import { SignupResponseType } from 'src/models/Account/Signup';
import { ResponseUserType } from 'src/models/Account/User';
import { RecaptchaViewModelType } from 'src/models/common';
import { InitialCommonStateType } from 'src/store/commonPart/slice';
import { PubNubMessage } from 'src/utils/PubNub';
import { SafeAnyType } from 'src/utils/safeAny';
import { tryCatch } from 'src/utils/tryCatch';
import { PlusSubscriptionSliceStateType } from 'src/store/plusSubscription/slice';
import { UiToastType } from 'src/components/UI/toasts';

declare global {
  interface Window {
    dataLayer: {
      push: (event: SafeAnyType) => void;
    };

    EventAggregator: {
      publish: (event: string, data: SafeAnyType) => void;
      subscribe: (channel: string, callback: (id: string, message: PubNubMessage) => void) => number | string;
      unsubscribe: (token: string) => void;
    };

    RallyUp: {
      ChameleonManager: {
        TrackEvent: (s: string) => void;
      };
      UserPilotManager: {
        TrackEvent: (s: string) => void;
      };
      PubNub: {
        JoinChannel: (channel: string) => unknown;
        LeaveChannel: (channel: string) => unknown;
      };
      FacebookPixelManager: {
        TrackPartnerEvent: (type: string) => void;
      };
      KustomerChatManager: {
        Open: () => void;
      };
      ChatWidgetManager: {
        Open: () => void;
      };
      SocialLinksController: {
        Facebook: (s: string) => void;
        Twitter: (s: string) => void;
        LinkedIn: (s: string) => void;
        Link: (s: string) => void;
      };
      SocialLinksController_26794: {
        Facebook: (s: string) => void;
        Twitter: (s: string) => void;
        LinkedIn: (s: string) => void;
        Link: (s: string, t?: string | null, i?: boolean) => void;
        Email: (subject?: string, body?: string) => void;
        Mobile: (s: string, title?: string, text?: string) => void;
      };
    };

    Kustomer?: {
      createConversation: (options: {
        assistantId?: string;
        message?: string;
        customAttributes?: Record<string, SafeAnyType>;
      }) => void;
    };

    FS?: {
      anonymize: () => void;
      identify: (uuid: string, props: Record<string, unknown>) => void;
    };

    spa: {
      signin: (data: SignInRequestType, callback: (response: SigninResponseType | null) => void) => void;
      signout: (callback: (response: string | null) => void) => void;
      signup: (
        data: {
          email: string;
          firstName: string;
          lastName: string;
          password: string;
          confirmPassword: string;
          marketingOptIn: boolean;
          recaptchaToken: string;
          RecaptchaViewModel: RecaptchaViewModelType;
        },
        callback: (response: SignupResponseType | null) => void
      ) => void;
      /** call this after signout from any part of the app */
      onSignout: () => void;
      showLoginModal: (v: boolean) => void;
      showTemplateGalleryModal: (calledFrom?: InitialCommonStateType['templateGalleryModalCalledFrom']) => void;

      /* plus subscription */
      showExclusiveModal: (type: NonNullable<PlusSubscriptionSliceStateType['modal']['type']>) => void;
      showPlanSelectionModal: () => void;
      showUpgradeNowModal: () => void;
      showDonorProfileOverlay: (id: number) => void;
    };

    toast: {
      success: UiToastType;
      error: UiToastType;
      info: UiToastType;
    };

    uetq: SafeAnyType;
  }
}

/** notify knockout when a user changes */
export const eventPublishAuthChanged = (data: ResponseUserType | null): void => {
  tryCatch(() => {
    window.EventAggregator.publish('Authentication_Changed', data);
  });
};

export const eventPublishShowPlusSubscriptionTrialBanner = (data: ResponseUserType | null): void => {
  tryCatch(() => {
    window.EventAggregator.publish('ShowPlusSubscriptionTrialBanner', data);
  });
};

export const openChatWidget = (): void => {
  tryCatch(() => {
    window.RallyUp.ChatWidgetManager.Open();
  });
};

/** TODO: delete after adding the chat manager icon component */
export const toggleChatWidgetIcon = (show: boolean): void => {
  tryCatch(() => {
    if (show) {
      document.body.classList.remove('hide-chat-widget');
    } else {
      document.body.classList.add('hide-chat-widget');
    }
  });
};

export const createKustomerConversation = (options: {
  assistantId?: string;
  message?: string;
  customAttributes?: Record<string, SafeAnyType>;
}): void => {
  tryCatch(() => {
    if (window.Kustomer) {
      window.Kustomer.createConversation(options);
    }
  });
};
